import React, { useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  DownloadIcon,
} from "@heroicons/react/outline";
import { Navigation, Pagination, Thumbs, EffectFade } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import breakpoints from "@/constants/breakpoinst";
import useWindowSize from "@/hooks/useWindowSize";
import Button from "@/components/actions/Button";
import { Listbox } from "@headlessui/react";
import * as Text from "@/components/text";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import "swiper/css/effect-fade";

export default function Editions(ediciones) {
  const windowSize = useWindowSize();
  const beforeRef = useRef(null);
  const paginationRef = useRef(null);
  const afterRef = useRef(null);
  const [slideIndex, setIndex] = useState(0);
  const [selectedGuide, setSelectedGuide] = React.useState(null);
  const [showOptions, setShowOptions] = React.useState(false);

  return (
    <div className="w-full pt-10 sm:pb-[32px] lg:pb-[120px]">
      <div className="w-full font-light leading-[42px] tracking-[-0.8px] sm:text-center sm:text-[24px] md:text-[32px] lg:text-left lg:text-[40px]">
        Ediciones anteriores
      </div>
      <div className="flex w-full justify-between sm:pt-2 md:pt-4 lg:pt-8">
        {windowSize.width > breakpoints.lg ? (
          <button ref={beforeRef} className="left-8 z-50 ">
            <ArrowLeftIcon className="h-4 w-4" />
          </button>
        ) : null}
        <div
          className="z-50 w-full cursor-pointer justify-start sm:grid sm:grid-cols-2 sm:gap-1 md:flex md:gap-3 md:px-3"
          ref={paginationRef}
        ></div>
        {windowSize.width > breakpoints.lg ? (
          <button ref={afterRef} className="right-auto">
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <Swiper
        spaceBetween={30}
        effect={"fade"}
        loop={true}
        onActiveIndexChange={({ realIndex }) => {
          setIndex(realIndex - 1);
        }}
        slidesPerView={1}
        modules={[EffectFade, Thumbs, Navigation, Pagination]}
        pagination={{
          clickable: true,
          type: "bullets",
          bulletClass: "my-bullet",
          el: paginationRef.current,
          renderBullet: function (index) {
            return (
              '<div class="my-bullet px-3 py-1 rounded-full border border-neutral-400 active:text-white active:bg-neutral-500 ">' +
              ediciones?.editions[index]?.title +
              "</div>"
            );
          },
          bulletActiveClass: "bg-black text-white",
        }}
        navigation={{
          enabled: false,
          nextEl: afterRef.current,
          prevEl: beforeRef.current,
          hiddenClass: "hidden",
          disabledClass: "text-neutral-50 border-neutral-50",
        }}
      >
        {ediciones?.editions?.map((edicion) => {
          return (
            <SwiperSlide
              key={edicion.id}
              className="flex h-[533px] w-full justify-center pt-8"
            >
              <div className="flex max-w-[974px] items-center justify-center sm:w-[328px] sm:flex-col md:w-full md:flex-row">
                <div className="w-full sm:h-[259px] sm:max-w-[184px] md:mr-8 md:h-[360px] md:max-w-[256px] lg:h-[533px] lg:max-w-[381px]">
                  <Image
                    width={1200}
                    height={720}
                    src={edicion.preview_image.data.attributes.url}
                    className="h-full w-full object-cover"
                    alt={edicion.id}
                  ></Image>
                </div>
                <div className="flex flex-col bg-white sm:w-[328px] sm:text-center md:text-left lg:h-full lg:w-full">
                  <span className="font-bold leading-10 sm:text-[28px] md:text-[32px] lg:pb-4 lg:text-4xl">
                    {edicion.title}
                  </span>
                  <span className="font-bold sm:text-[16px] sm:leading-8 md:text-xl md:leading-10 lg:pb-3 lg:text-2xl">
                    {edicion.subtitle}&nbsp;
                  </span>
                  <span className="font-bold leading-6 sm:text-[14px] md:mb-5 md:text-base lg:mb-8 lg:text-lg">
                    {edicion.header}&nbsp;
                  </span>
                  <span className="leading-6 sm:mb-4 sm:text-base md:mb-0 lg:mb-8 lg:text-lg">
                    {edicion.description}&nbsp;
                  </span>
                  <div className="flex sm:flex-col sm:items-center md:items-start lg:flex-row ">
                    {edicion?.download_urls?.length > 0 ? (
                      <>
                        <Listbox
                          as="div"
                          className="w-[225px] sm:mt-5 lg:mr-6 lg:mt-10"
                          value={selectedGuide}
                          onChange={setSelectedGuide}
                        >
                          <Listbox.Button
                            as={Button}
                            color="secondary-light"
                            className="h-[52px] w-full border border-neutral-900"
                            label="Descargar programas"
                            trailingIcon={
                              <ChevronDownIcon width={32} height={32} />
                            }
                            onClick={() => setShowOptions(!showOptions)}
                          ></Listbox.Button>
                          <Listbox.Options as="div">
                            {showOptions &&
                              edicion?.download_urls.map((option, i) => (
                                <Link
                                  key={i}
                                  href={option.download_url}
                                  target="_blank"
                                >
                                  <div className="flex cursor-pointer items-center border border-t-0 border-gray-500 p-3 hover:bg-neutral-50 focus:ring-0 active:bg-black active:text-white">
                                    <div className="group grow ">
                                      <Text.Label
                                        size="sm"
                                        as="p"
                                        className="text-neutral-1000 group-active:text-white"
                                      >
                                        {option.name}
                                      </Text.Label>
                                    </div>
                                    <div className="text-neutral-800 group-active:text-white/60">
                                      <DownloadIcon className="h-5 w-5" />
                                    </div>
                                  </div>
                                </Link>
                              ))}
                          </Listbox.Options>
                        </Listbox>
                      </>
                    ) : null}
                    {edicion?.link && (
                      <Link key={edicion?.link} href={edicion?.link} passHref>
                        <Button
                          color="terciary-light"
                          size="lg"
                          className="w-[225px] sm:mt-5 lg:mr-6 lg:mt-10"
                          trailingIcon={<ArrowRightIcon />}
                          label="Ver catálogo de películas"
                        ></Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}
